import { Component, inject, signal, computed, CUSTOM_ELEMENTS_SCHEMA, resource } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AppMetrics, MetricItem } from '../../../shared/components/app-metrics/app-metrics';
import { AppModal } from '../../../shared/components/app-modal/app-modal';
import { Router, RouterLink } from '@angular/router';
import { lastValueFrom, tap } from 'rxjs';
import { UserService } from '../../../shared/services/user.service';
import { AppDataTable, DataColumn, PaginationState } from '../../../shared/components/app-data-table/app-data-table';
import { AppButton } from '../../../shared/components/app-button/app-button';
import { AuthService } from '../../../core/services/auth.service';
import { BusinessContextService } from '../../../core/services/business-context.service';

import { AppConfirmDialog } from '../../../shared/components/app-confirm-dialog/app-confirm-dialog';
import { ToastService } from '../../../core/services/toast.service';

@Component({
  selector: 'app-user-list',
  standalone: true,
  imports: [CommonModule, AppDataTable, AppButton, RouterLink, AppMetrics, AppModal, AppConfirmDialog],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './user-list.html'
})
export class UserList {
  private userService = inject(UserService);
  private authService = inject(AuthService);
  public bizCtx = inject(BusinessContextService);
  private router = inject(Router);
  private toast = inject(ToastService);

  // ── Reenvío de Correo (Diálogo) ─────────────────────────────────────────
  showResendConfirm = signal(false);
  userToResend = signal<any | null>(null);
  isResending = signal(false);

  // ── Parámetros de consulta ──────────────────────────────────────────────
  page = signal(1);
  limit = signal(10);
  searchQ = signal('');

  // ── Resource (Angular 19 Pattern) ───────────────────────────────────────
  userResource = resource({
    params: () => ({
      page: this.page(),
      limit: this.limit(),
      search: this.searchQ()
    }),
    loader: async ({ params }) => {
      const { page, limit, search } = params;
      return lastValueFrom(
        this.userService.getAll({ page, limit, search }).pipe(
          tap((response: any) => {
            const rawData = response.items || response.data || [];
            const isSA = this.isSuperAdmin();

            this.rawUsers.set(rawData.map((item: any) => {
              const u = item.userId && typeof item.userId === 'object' ? item.userId : item;
              const isSA = u.isSuperAdmin === true;
              return {
                id: u._id || u.id,
                fullName: u.fullName,
                email: u.email,
                isActive: item.isActive !== false, // Safe Staff fallback
                status: u.status || 'active',
                isSuperAdmin: isSA,
                isEmailVerified: u.isEmailVerified,
                permissionsCount: isSA ? 'Total' : (item.permissions || []).length,
                permissions: item.permissions || [],
                source: item.userId ? 'Staff' : 'user',
                createdAt: u.createdAt || item.createdAt
              };
            }));

            this.total.set(response.total || this.rawUsers().length);
          })
        )
      );
    }
  });

  // ── Estado ──────────────────────────────────────────────────────────────
  rawUsers = signal<any[]>([]);
  total = signal(0);
  loading = this.userResource.isLoading;

  showDetails = signal(false);
  selectedUser = signal<any | null>(null);

  viewDetails(user: any) {
    this.selectedUser.set(user);
    this.showDetails.set(true);
  }

  getModulePermissions(user: any): { module: string, actions: string[] }[] {
    const perms = user.permissions || [];
    const grouped: { [key: string]: string[] } = {};

    perms.forEach((p: any) => {
      if (typeof p === 'string') {
        const parts = p.split(':');
        grouped[parts[0]] = grouped[parts[0]] || [];
        grouped[parts[0]].push(parts[1] || 'all');
      }
    });

    return Object.keys(grouped).map(key => ({
      module: key,
      actions: grouped[key]
    }));
  }

  readonly displayUsers = computed(() => this.rawUsers());
  readonly isSuperAdmin = computed(() => this.authService.currentUserValue?.isSuperAdmin === true);
  readonly canManageUsers = computed(() => this.isSuperAdmin() || (this.bizCtx.permissions() || []).includes('*'));

  readonly metricsItems = computed<MetricItem[]>(() => [
    {
      label: this.isSuperAdmin() ? 'Directorio Global' : 'Plantilla Activa',
      value: this.total(),
      icon: this.isSuperAdmin() ? 'solar:globus-bold-duotone' : 'solar:users-group-rounded-bold-duotone',
      variant: 'primary'
    }
  ]);

  pagination = computed<PaginationState>(() => ({
    current: this.page(),
    limit: this.limit(),
    total: this.total()
  }));

  columns: DataColumn[] = [
    { key: 'fullName', title: 'Usuario / Miembro', dataIndex: 'fullName' },
    { key: 'email', title: 'Identificación / Correo', dataIndex: 'email' },
    { key: 'status', title: 'Estado', dataIndex: 'isActive' },
    { key: 'actions', title: '', align: 'right', class: 'w-24' },
  ];

  handleSearch(query: string) {
    this.searchQ.set(query);
    this.page.set(1);
  }

  handlePageChange(page: number) {
    this.page.set(page);
  }

  handleLimitChange(limit: number) {
    this.limit.set(limit);
    this.page.set(1);
  }

  getUserInitials(name: string): string {
    return name?.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase() || 'U';
  }

  deleteUser(id: string) {
    const msg = this.isSuperAdmin()
      ? '¿Estás seguro de eliminar este usuario del sistema?'
      : '¿Estás seguro de quitar el acceso a este colaborador del negocio?';

    if (confirm(msg)) {
      this.userService.delete(id).subscribe({
        next: () => this.userResource.reload(),
        error: (err) => {
          console.error(err);
          this.userResource.reload();
        }
      });
    }
  }

  onResendClick(user: any) {
    this.userToResend.set(user);
    this.showResendConfirm.set(true);
  }

  confirmResend() {
    const user = this.userToResend();
    if (!user) return;

    this.isResending.set(true);
    this.authService.resendVerification(user.id).subscribe({
      next: () => {
        this.showResendConfirm.set(false);
        this.isResending.set(false);
        this.toast.show('Éxito', `Correo de verificación enviado a ${user.email}`, 'success');
      },
      error: (err: any) => {
        this.showResendConfirm.set(false);
        this.isResending.set(false);
        this.toast.show('Error', 'No se pudo enviar el correo: ' + (err.error?.message || err.message), 'error');
      }
    });
  }
}
